// ─── widgets/image.js ─────────────────────────────────────────────────────────
// Creates AI_IMAGE_WIDGET objects from [data-widget="image"] elements.

import shortid from 'shortid';
import { AI_IMAGE_WIDGET } from 'constants/widgetTypes';
import { cs, getAbsoluteRect } from '../utils/dom.js';
import { extractRGBAFromColor, extractShadowProperties, extractBorderRadius } from '../utils/style.js';

/**
 * @param {{ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, prompt, aspectRatio }}
 */
export function createImageWidgetObj({ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, prompt, aspectRatio }) {
    const rect  = getAbsoluteRect(el);
    const mRect = mobileEl
        ? getAbsoluteRect(mobileEl)
        : {
            width:  Math.min(rect.width, 335),
            height: rect.height * (Math.min(rect.width, 335) / rect.width),
            top:    rect.top,
            left:   Math.min(rect.left, 20),
        };

    let src = '';
    try { src = new URL(el.getAttribute('src') || '', document.baseURI).href; }
    catch { src = el.getAttribute('src') || ''; }

    // Use parent div for border/shadow when available
    const styleEl = (el.parentElement?.tagName === 'DIV' ? el.parentElement : null) || el;
    const style   = cs(styleEl);
    const border  = {
        sides:  null,
        color:  extractRGBAFromColor(style.borderColor),
        width:  parseFloat(style.borderWidth || '0').toString(),
        radius: extractBorderRadius(style),
    };
    const shadow = extractShadowProperties(style.boxShadow);

    const mOff = mobileOffsetRect || offsetRect;
    const ix = Math.round(rect.left  - (offsetRect.left || 0));
    const iy = Math.round(rect.top   - (offsetRect.top  || 0));
    const mx = Math.round(mRect.left - (mOff.left || 0));
    const my = Math.round(mRect.top  - (mOff.top  || 0));

    return {
        uid:           `widget_${shortid.generate()}`,
        type:          AI_IMAGE_WIDGET,
        name:          'Image',
        parent:        parentUid,
        order:         orderRef.value++,
        systemDefined: false,
        capabilities:  null,
        fonts:         null,
        actions:       null,
        parameter:     null,
        properties: {
            desktop: {
                visible: true,
                layout: {
                    transform: { translate: `${ix}px,${iy}px`, rotate: '0deg', scale: '1,1' },
                    width:  Math.round(rect.width)  + 'px',
                    height: Math.round(rect.height) + 'px',
                },
                shadow, border, crop: null, alt: el.alt || null, position: null, animation: null,
            },
            mobile: {
                visible: true,
                layout: {
                    transform: { translate: `${mx}px,${my}px`, rotate: '0deg' },
                    width:  Math.round(mRect.width)  + 'px',
                    height: Math.round(mRect.height) + 'px',
                },
                shadow, border, crop: null, alt: el.alt || null, position: null, animation: null,
            },
        },
        content: {
            contentType: 'image/png',
            type:        'image',
            width:       rect.width,
            height:      rect.height,
            url:         src,
            prompt,
            aspectRatio,
        },
    };
}